import React, { useEffect, useState } from 'react'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import Button from '../../../../components/Form/Button'
import Input from '../../../../components/Form/Input'

export type ServiceAgreementCloseTrigger = 'deactivate' | 'endDate'

export interface ServiceAgreementCloseRequest {
  meterServiceId: string
  serviceAgreementId?: string
  trigger: ServiceAgreementCloseTrigger
  /** ISO date yyyy-mm-dd when trigger is endDate, or today when deactivate + set end date. */
  proposedEndDate: string
  currentlyActive: boolean
  existingEndDate?: string | null
}

export interface ServiceAgreementCloseChoices {
  setEndDate: boolean
  deactivate: boolean
  endDate: string
}

interface ServiceAgreementCloseModalProps {
  isOpen: boolean
  request: ServiceAgreementCloseRequest | null
  isSubmitting?: boolean
  onClose: () => void
  onConfirm: (choices: ServiceAgreementCloseChoices) => void | Promise<void>
}

export const todayIsoDate = (): string => {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

const isValidIsoDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return (
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
  )
}

export const isEndDateTodayOrPast = (isoDate: string | null | undefined): boolean => {
  if (isoDate == null || isoDate === '') return false
  return isoDate <= todayIsoDate()
}

const ServiceAgreementCloseModal: React.FC<ServiceAgreementCloseModalProps> = ({
  isOpen,
  request,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const [shouldSetEndDate, setShouldSetEndDate] = useState(true)
  const [deactivate, setDeactivate] = useState(true)
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    if (!isOpen || request == null) return
    setEndDate(request.proposedEndDate)
    if (request.trigger === 'deactivate') {
      setDeactivate(true)
      const needsEndDate =
        request.existingEndDate == null ||
        request.existingEndDate === '' ||
        !isEndDateTodayOrPast(request.existingEndDate)
      setShouldSetEndDate(needsEndDate)
    } else {
      setShouldSetEndDate(true)
      setDeactivate(request.currentlyActive)
    }
  }, [isOpen, request])

  if (!isOpen || request == null) return null

  const endDateValid = !shouldSetEndDate || isValidIsoDate(endDate)
  const canConfirm = (shouldSetEndDate || deactivate) && endDateValid
  const saLabel = request.serviceAgreementId ? `SA ${request.serviceAgreementId}` : 'this service agreement'

  return (
    <CustomContentModal isOpen={isOpen} onClose={isSubmitting ? () => undefined : onClose}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Close service agreement?</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {request.trigger === 'deactivate'
            ? `You are deactivating ${saLabel}. Choose what to apply:`
            : `End date is today or in the past for ${saLabel}. Choose what to apply:`}
        </p>

        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0"
                checked={shouldSetEndDate}
                disabled={isSubmitting}
                onChange={(e) => setShouldSetEndDate(e.target.checked)}
              />
              <span>
                Set end date
                {request.trigger === 'deactivate' &&
                request.existingEndDate &&
                isEndDateTodayOrPast(request.existingEndDate)
                  ? ' (already set)'
                  : null}
              </span>
            </label>
            {shouldSetEndDate ? (
              <div className="pl-6">
                <Input
                  type="date"
                  label="End date"
                  value={endDate}
                  disabled={isSubmitting}
                  required
                  error={!endDateValid ? 'Must be YYYY-MM-DD' : undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            ) : null}
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={deactivate}
              disabled={isSubmitting || !request.currentlyActive}
              onChange={(e) => setDeactivate(e.target.checked)}
            />
            <span>Deactivate service agreement (meter service)</span>
          </label>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" color="slate" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canConfirm || isSubmitting}
            onClick={() =>
              void onConfirm({
                setEndDate: shouldSetEndDate,
                deactivate,
                endDate,
              })
            }
          >
            {isSubmitting ? 'Saving…' : 'Confirm'}
          </Button>
        </div>
      </div>
    </CustomContentModal>
  )
}

export default ServiceAgreementCloseModal
